import * as React from 'react';
import { ClickAwayListener, Fade, Popper, PopperPlacementType, SxProps, Theme, Typography } from '@mui/material';
import { useHandleCapturedEventsRef } from '../../hooks';
import { customArcherColors, hexToRGB, theme } from '../../utils';
import { IconKey } from '../Icon';
import { IconButton } from '../IconButton';
import { LayoutGrid } from '../LayoutGrid';
import { Tooltip, TooltipProps } from '../Tooltip';

type IconSize = 'small' | 'medium' | 'large' | undefined;

export type InfoPopperProps = {
  buttonDataProp?: string;
  icon: IconKey;
  iconColor?: string;
  fontSize?: IconSize;
  tooltip?: TooltipProps['title'];
  disabled?: boolean;
  popperPosition: PopperPlacementType;
  className?: string;
  contentItem: any;
  childContentRefs?: React.RefObject<HTMLElement>[];
  overrideClickAwayRefs?: React.RefObject<boolean>[];
  sxButton?: SxProps<Theme>;
  sxPopper?: SxProps<Theme>;
};

export type CssProps = {
  activeAndFocusSetting: string;
  iconTrueColor: string;
  hoverSetting: string;
};

const keyListeners = { escape: 'Escape' };

const isAlert = (icon: string) => {
  return !!(icon && (icon === 'Info' || icon === 'Warning' || icon === 'Success' || icon === 'Error'));
};
const setActiveAndFocus = (iconTrueColor: string) => {
  return hexToRGB(iconTrueColor, 0.15);
};

const useStyles = (props: CssProps): Record<string, SxProps<Theme>> => ({
  popperContainer: {
    zIndex: 'modal',
    width: '425px',
    borderRadius: '3px',
    boxShadow: '0 2px 10px 0 rgba(33,33,33,.40)',
    padding: '1rem',
    backgroundColor: 'background.paper'
  },
  infoPopperButton: {
    padding: '0.5rem',
    backgroundColor: 'transparent',
    color: props.iconTrueColor,
    '&:hover': {
      borderRadius: '50%',
      backgroundColor: props.hoverSetting
    },
    '&:focus': {
      backgroundColor: props.activeAndFocusSetting
    }
  },
  infoPopperButtonActive: {
    padding: '0.5rem',
    color: props.iconTrueColor,
    backgroundColor: props.activeAndFocusSetting,
    '&:hover': {
      borderRadius: '50%',
      backgroundColor: props.hoverSetting
    },
    '&:focus': {
      backgroundColor: props.activeAndFocusSetting
    }
  }
});

const InfoPopperBase = ({
  buttonDataProp,
  icon,
  iconColor,
  fontSize,
  tooltip,
  disabled,
  popperPosition,
  className,
  contentItem,
  childContentRefs = [],
  overrideClickAwayRefs = [],
  sxButton,
  sxPopper
}: InfoPopperProps) => {
  const isAlertIcon = isAlert(icon);
  const iconTrueColor: any = isAlertIcon ? customArcherColors.getCustomAlertColors(iconColor) : iconColor || theme.palette.primary.main;
  const activeAndFocusSetting = setActiveAndFocus(iconTrueColor);
  const hoverSetting = hexToRGB(iconTrueColor, 0.07);

  const cssProps = { activeAndFocusSetting, iconTrueColor, hoverSetting };
  const { popperContainer, infoPopperButton, infoPopperButtonActive } = useStyles(cssProps);

  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [open, setOpen] = React.useState<boolean>(false);
  const [tooltipOpen, setTooltipOpen] = React.useState<boolean>(false);
  const [placement, setPlacement] = React.useState<PopperPlacementType>();
  const iconButtonRef = React.useRef<HTMLButtonElement>(null);

  const handleTooltipClose = () => {
    setTooltipOpen(false);
  };

  const handleTooltipOpen = () => {
    setTooltipOpen(true);
  };

  const handleClick = (newPlacement: PopperPlacementType) => (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    event.preventDefault();
    setAnchorEl(event.currentTarget);
    setOpen(prev => placement !== newPlacement || !prev);
    setPlacement(newPlacement);
    setTooltipOpen(false);
  };

  const handleClickAway = React.useCallback(
    (event: MouseEvent | TouchEvent) => {
      const shouldOverrideHandleClickAway = overrideClickAwayRefs.some(overrideRef => overrideRef.current);
      if (!shouldOverrideHandleClickAway) {
        const shouldKeepPopperOpen = childContentRefs.some(contentRef => contentRef.current && contentRef.current.contains(event.target as Node));
        setOpen(shouldKeepPopperOpen);
      }
    },
    [setOpen, childContentRefs]
  );

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === keyListeners.escape) {
      setOpen(false);
      if (iconButtonRef.current) {
        iconButtonRef.current.blur();
      }
    }
  };

  const keyDownWrapperRef = useHandleCapturedEventsRef<HTMLDivElement>(null, { keydown: [handleKeyDown] });
  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <div ref={keyDownWrapperRef}>
        <LayoutGrid container direction="row" justify="flex-start">
          <Tooltip title={!open ? tooltip || '' : ''} open={tooltipOpen} onClose={handleTooltipClose} onOpen={handleTooltipOpen}>
            <IconButton
              sx={[open ? infoPopperButtonActive : infoPopperButton, sxButton] as SxProps<Theme>}
              icon={icon === 'Info' ? 'InfoOutlined' : icon}
              onClick={handleClick(popperPosition || 'bottom')}
              ref={iconButtonRef}
              fontSize={fontSize || 'large'}
              dataProp={buttonDataProp || 'icon'}
              className="infoPopperFilter"
              disabled={disabled || false}
              aria-label={tooltip}
            />
          </Tooltip>
        </LayoutGrid>
        <Popper
          sx={[popperContainer, sxPopper] as SxProps<Theme>}
          open={open}
          anchorEl={anchorEl}
          placement={placement}
          className={className}
          aria-describedby="infopopper"
          transition
          disablePortal
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={350}>
              <div>
                {typeof contentItem === 'string' ? (
                  <Typography variant="body1" color="textPrimary" id="infopopper">
                    {contentItem || ''}
                  </Typography>
                ) : (
                  contentItem
                )}
              </div>
            </Fade>
          )}
        </Popper>
      </div>
    </ClickAwayListener>
  );
};

const InfoPopper = React.memo(InfoPopperBase);

InfoPopper.displayName = 'InfoPopper';

export { InfoPopper };
